const money = new Map<string, Intl.NumberFormat>();

/**
 * Money as Colombians read it: `$ 1.500.000` for COP, no decimals.
 *
 * @param {number} value -- The amount.
 * @param {string} currency -- ISO 4217 code, `COP` by default.
 * @returns The formatted amount.
 */
export const formatMoney = (value: number, currency = 'COP'): string => {
  let formatter = money.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'COP' ? 0 : 2,
    });
    money.set(currency, formatter);
  }
  return formatter.format(value);
};

/**
 * A `YYYY-MM-DD` date as `5 sept 2026`, without timezone shifts.
 *
 * @param {string | null | undefined} value -- The ISO date.
 * @returns The formatted date, or an empty string.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

/**
 * An ISO timestamp as `5 sept 2026, 14:03`.
 *
 * @param {string | null | undefined} value -- The ISO timestamp.
 * @returns The formatted moment, or an empty string.
 */
export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '';
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

/**
 * Today as `YYYY-MM-DD` in the browser's timezone.
 *
 * @returns The date.
 */
export const today = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
